import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { User, Lock, Mail, Phone, Heart, Building2, Sparkles, Star, Users, Fingerprint, ScanFace, Rocket, Telescope, PlayCircle, X, BookOpen, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PhoneValidation from "../components/common/PhoneValidation";
import { AnimatedLogo, AnimatedText } from "../components/common/AnimatedLogo";
import { MockupLogo } from "../components/common/LogoAnimationMockups";
import { ParentIcon, TeacherIcon } from "../components/ui/AnimatedElements";
import useBiometricAuth from "../hooks/useBiometricAuth";
import { usePublicModules } from "../hooks/useModuleAccess";
import { getAppSlogan } from "../config/branding";

export default function LoginPage({ onLogin }) {
  const [slogan, setSlogan] = useState(getAppSlogan());
  useEffect(() => {
    const handler = () => setSlogan(getAppSlogan());
    window.addEventListener("branding-updated", handler);
    return () => window.removeEventListener("branding-updated", handler);
  }, []);
  const [mode, setMode] = useState("login");
  const [userType, setUserType] = useState("parent");
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  
  // Kawalan module: biometric_login (berkuatkuasa semua user)
  const { isPublicModuleOn, loading: publicModulesLoading } = usePublicModules();
  const biometricLoginOn = isPublicModuleOn('biometric_login');
  const registrationOn = isPublicModuleOn('pending_approval');
  
  // Biometric auth hook
  const { 
    isSupported: biometricSupported, 
    isEnabled: biometricEnabled, 
    biometricType,
    authenticateWithBiometric,
    canUseBiometricLogin,
    isLoading: biometricHookLoading
  } = useBiometricAuth();
  
  const [parentData, setParentData] = useState({
    phone: "", password: "", name: "", spouse_name: "", email: "", branch: ""
  });
  
  const [staffData, setStaffData] = useState({
    email: "", password: "", name: "", role: "guru", branch: ""
  });
  
  const [forgotEmail, setForgotEmail] = useState("");
  const [phoneValidationPassed, setPhoneValidationPassed] = useState(true);

  // Video tutorial di halaman login - hardcoded fail tempatan sebagai lalai,
  // URL superadmin (jika ditetapkan) akan menggantikannya
  const DEFAULT_TUTORIAL_URL = "/tutorial-video-ibubapa.mp4?v=9";
  const [tutorialUrl, setTutorialUrl] = useState(DEFAULT_TUTORIAL_URL);
  const [showTutorial, setShowTutorial] = useState(false);
  // Modul biometrik hanya untuk pakej FULL (dari /public/module-status)
  const [biometricModuleOn, setBiometricModuleOn] = useState(false);

  useEffect(() => {
    const fetchModuleStatus = async () => {
      try {
        const res = await axiosInstance.get('/public/module-status');
        setBiometricModuleOn(res.data?.modules?.biometric_login !== false);
      } catch (e) {
        // Abaikan - banner biometrik kekal tersembunyi jika gagal
      }
    };
    fetchModuleStatus();
  }, []);

  useEffect(() => {
    const fetchTutorialUrl = async () => {
      try {
        const res = await axiosInstance.get('/settings/app-name');
        setTutorialUrl(res.data?.tutorial_video_url || DEFAULT_TUTORIAL_URL);
      } catch (e) {
        // Abaikan - video hardcoded tempatan tetap dipaparkan
      }
    };
    fetchTutorialUrl();
  }, []);

  // Tukar URL YouTube (watch/youtu.be/shorts) kepada URL embed untuk paparan dalam aplikasi
  const getEmbedUrl = (url) => {
    if (!url) return "";
    try {
      const u = new URL(url);
      let videoId = "";
      if (u.hostname.includes("youtu.be")) {
        videoId = u.pathname.split("/")[1] || "";
      } else if (u.hostname.includes("youtube.com")) {
        if (u.pathname.startsWith("/watch")) videoId = u.searchParams.get("v") || "";
        else if (u.pathname.startsWith("/shorts/") || u.pathname.startsWith("/embed/")) videoId = u.pathname.split("/")[2] || "";
      }
      if (videoId) return `https://www.youtube.com/embed/${videoId}?rel=0`;
    } catch (e) { /* bukan URL sah */ }
    return url;
  };

  const isDirectVideo = (url) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");

  // AUTO BIOMETRIC LOGIN - trigger Face ID/fingerprint automatically when page loads
  // Papar mesej jika cawangan dinonaktifkan sementara
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('branch_disabled') === 'true') {
      toast.error("Cawangan anda dinonaktifkan sementara oleh pentadbir. Sila hubungi pihak pengurusan.", { duration: 8000 });
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  useEffect(() => {
    // Skip if already attempted, loading, or not ready
    if (autoLoginAttempted || biometricHookLoading || biometricLoading) {
      return;
    }
    
    // Kawalan module: biometric_login OFF - jangan auto-login
    if (publicModulesLoading || !biometricLoginOn) {
      return;
    }
    
    // Check if biometric is available and enabled
    if (canUseBiometricLogin()) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(async () => {
        setAutoLoginAttempted(true);
        setBiometricLoading(true);
        
        try {
          const result = await authenticateWithBiometric();
          toast.success("Log masuk biometric berjaya! 🎉");
          onLogin(result.user, result.token);
        } catch (error) {
          // Silent fail for auto-login - user can still login manually
          console.log("Auto biometric login skipped:", error.message);
          // Only show toast if user actively cancelled
          if (error.name === 'NotAllowedError') {
            // User cancelled - don't show error, they can login manually
          } else {
            toast.info("Sila log masuk dengan kata laluan");
          }
        } finally {
          setBiometricLoading(false);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [canUseBiometricLogin, biometricHookLoading, autoLoginAttempted, authenticateWithBiometric, onLogin, biometricLoading, publicModulesLoading, biometricLoginOn]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axiosInstance.get('/public/branches');
        setBranches(response.data);
      } catch (error) {
        console.error("Error fetching branches");
      }
    };
    fetchBranches();
  }, []);

  const handleParentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/auth/parent/login", {
        phone: parentData.phone, password: parentData.password
      });
      toast.success("Log masuk berjaya! 🎉");
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nombor telefon atau password tidak betul");
    } finally {
      setLoading(false);
    }
  };

  const handleParentRegister = async (e) => {
    e.preventDefault();
    if (parentData.password.length < 6) {
      toast.error("Password mestilah sekurang-kurangnya 6 aksara");
      return;
    }
    if (!parentData.branch) {
      toast.error("Sila pilih cawangan");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post("/auth/parent/register", parentData);
      if (response.data.requires_verification) {
        toast.success("Pendaftaran berjaya! Sila semak email untuk pengesahan. 📧");
        setMode("login");
      } else if (response.data.requires_approval) {
        toast.success("Pendaftaran berjaya! Sila tunggu kelulusan daripada Admin. ⏳");
        setMode("login");
      } else {
        toast.success("Pendaftaran berjaya! 🎉");
        onLogin(response.data.user, response.data.token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat semasa pendaftaran");
    } finally {
      setLoading(false);
    }
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/auth/login", {
        email: staffData.email, password: staffData.password
      });
      toast.success("Log masuk berjaya! 🎉");
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email atau password tidak betul");
    } finally {
      setLoading(false);
    }
  };

  const handleStaffRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post("/auth/register", {
        email: staffData.email, password: staffData.password, name: staffData.name, role: staffData.role
      });
      toast.success("Pendaftaran berjaya! Sila tunggu kelulusan dari Superadmin. ✨");
      setMode("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat semasa pendaftaran");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Link reset password telah dihantar ke email anda. 📧");
      setMode("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat");
    } finally {
      setLoading(false);
    }
  };

  // Biometric login handler
  const handleBiometricLogin = async () => {
    if (!canUseBiometricLogin()) {
      toast.error("Log masuk biometric tidak tersedia");
      return;
    }
    
    setBiometricLoading(true);
    try {
      const result = await authenticateWithBiometric();
      toast.success("Log masuk biometric berjaya! 🎉");
      onLogin(result.user, result.token);
    } catch (error) {
      toast.error(error.message || "Pengesahan biometric gagal");
    } finally {
      setBiometricLoading(false);
    }
  };

  // Get biometric icon and name based on type
  // Android returns 'biometric' which covers both fingerprint and face
  const BiometricIcon = biometricType === 'face' ? ScanFace : Fingerprint;
  const getBiometricName = () => {
    if (biometricType === 'face') return 'Face ID';
    if (biometricType === 'biometric') return 'Biometrik'; // Android: fingerprint or face
    return 'Cap Jari';
  };

  // Floating decorations - Fun School theme (rainbow/student/balloon/books)
  const decorations = [
    { emoji: '🌈', left: '5%', top: '15%', delay: 0, depth: 30 },
    { emoji: '🎈', left: '90%', top: '20%', delay: 0.5, depth: 45 },
    { emoji: '📚', left: '8%', top: '75%', delay: 1, depth: 25 },
    { emoji: '🎓', left: '85%', top: '70%', delay: 1.5, depth: 40 },
    { emoji: '🧑‍🎓', left: '15%', top: '45%', delay: 0.3, depth: 55 },
    { emoji: '✏️', left: '80%', top: '45%', delay: 0.8, depth: 20 },
    { emoji: '👩‍🎓', left: '25%', top: '85%', delay: 1.2, depth: 50 },
    { emoji: '📖', left: '70%', top: '85%', delay: 0.6, depth: 35 },
  ];

  // Mouse parallax + Gyroscope (mobile tilt) effect - smooth 60fps via rAF + lerp
  useEffect(() => {
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    let rafId;

    const handleMouseMove = (e) => {
      // Normalize mouse position to -1..1 from screen center
      target.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.y = (e.clientY / window.innerHeight) * 2 - 1;
    };

    const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

    const handleOrientation = (e) => {
      if (e.gamma === null || e.beta === null) return;
      // gamma: left-right tilt (-90..90), beta: front-back tilt (-180..180)
      // Offset beta by 45deg (natural phone holding angle)
      target.x = clamp(e.gamma / 30, -1, 1);
      target.y = clamp((e.beta - 45) / 30, -1, 1);
    };

    // iOS 13+ requires permission via user gesture; Android works directly
    const requestGyroPermission = () => {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission()
          .then((state) => {
            if (state === 'granted') {
              window.addEventListener('deviceorientation', handleOrientation);
            }
          })
          .catch(() => {});
      }
      window.removeEventListener('touchstart', requestGyroPermission);
    };

    if (typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      // iOS: request on first touch
      window.addEventListener('touchstart', requestGyroPermission, { once: true });
    } else {
      // Android & others: listen directly
      window.addEventListener('deviceorientation', handleOrientation);
    }

    const animate = () => {
      // Smooth interpolation (lerp) towards target
      current.x += (target.x - current.x) * 0.06;
      current.y += (target.y - current.y) * 0.06;

      const els = document.querySelectorAll('[data-parallax-depth]');
      els.forEach((el) => {
        const depth = parseFloat(el.getAttribute('data-parallax-depth')) || 30;
        el.style.transform = `translate3d(${(-current.x * depth).toFixed(2)}px, ${(-current.y * depth).toFixed(2)}px, 0)`;
      });

      rafId = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    rafId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('deviceorientation', handleOrientation);
      window.removeEventListener('touchstart', requestGyroPermission);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{
      background: 'linear-gradient(180deg, #5BA8DC 0%, #7BBEE9 45%, #9AD2F3 80%, #BDE4FA 100%)'
    }}>
      {/* Colorful decorations - with mouse parallax */}
      {decorations.map((item, i) => (
        <div
          key={i}
          className="absolute"
          data-parallax-depth={item.depth}
          style={{
            left: item.left,
            top: item.top,
            willChange: 'transform',
          }}
        >
          <div
            className="text-4xl opacity-60"
            style={{
              animation: `floatDecor 4s ease-in-out infinite`,
              animationDelay: `${item.delay}s`,
            }}
          >
            {item.emoji}
          </div>
        </div>
      ))}
      
      {/* Cosmic nebula blobs */}
      <div className="absolute top-10 left-10 w-40 h-40 rounded-full blur-3xl" style={{ background: 'rgba(255,107,157,0.30)' }} />
      <div className="absolute top-32 right-16 w-48 h-48 rounded-full blur-3xl" style={{ background: 'rgba(111,231,221,0.25)' }} />
      <div className="absolute bottom-20 left-20 w-44 h-44 rounded-full blur-3xl" style={{ background: 'rgba(145,120,214,0.35)' }} />
      <div className="absolute bottom-32 right-10 w-36 h-36 rounded-full blur-3xl" style={{ background: 'rgba(255,201,60,0.25)' }} />
      
      {/* Sun - with parallax movement */}
      <div className="absolute top-8 right-8" data-parallax-depth="38" style={{ willChange: 'transform' }}>
        <div className="opacity-70" style={{ animation: 'spin 30s linear infinite' }}>
          <svg width="60" height="60" viewBox="0 0 100 100">
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
              <rect key={i} x="46" y="5" width="8" height="18" rx="4" fill="#FFDD75" transform={`rotate(${angle} 50 50)`} />
            ))}
            <circle cx="50" cy="50" r="25" fill="#FFDD75" />
          </svg>
        </div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-4 relative">
            <div 
              className="absolute inset-0 w-40 h-40 rounded-full blur-2xl"
              style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #FFC93C 50%, #6FE7DD 100%)', opacity: 0.45 }}
            />
            <div className="relative z-10">
              <MockupLogo
                mockup={1}
                src="/logo-destiny.png"
                alt="Taska Ummu Qaseh Logo"
                className="w-36 h-36 object-contain drop-shadow-2xl"
              />
            </div>
          </div>
          <h1 className="text-4xl font-black mb-1 whitespace-nowrap" style={{ fontFamily: "'Bungee', 'Comfortaa', Arial, sans-serif", letterSpacing: '0.5px' }}>
            <span style={{ color: '#FFC93C', textShadow: '0 0 20px rgba(255,201,60,0.5)' }}>TASKA</span>{' '}
            <span style={{ color: '#7357C9', textShadow: '0 0 20px rgba(115,87,201,0.5)' }}>UMMU QASEH</span>
          </h1>
          <p className="text-sm font-bold flex items-center justify-center gap-1" style={{ color: '#E6E3F7' }} data-testid="login-slogan">
            🌈 {slogan || "Because We Love"} ⭐
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="bg-white shadow-2xl p-6 relative overflow-hidden"
          style={{ 
            borderRadius: '32px',
            border: '3px solid #7357C9',
            boxShadow: '0 0 35px rgba(115,87,201,0.45), 0 0 60px rgba(115,87,201,0.2)'
          }}
        >
          {/* Rainbow top bar */}
          <div className="absolute top-0 left-0 right-0 h-2" style={{ background: 'linear-gradient(90deg, #4B3F8C, #FFC93C, #6FE7DD, #FF6B9D, #4B3F8C)', borderRadius: '28px 28px 0 0' }} />
          
          {/* User Type Selector */}
          {mode === "login" && (
            <div className="flex gap-3 mb-5">
              <button
                onClick={() => setUserType("parent")}
                data-testid="login-tab-parent"
                className={`flex-1 py-3 px-4 rounded-2xl font-bold text-sm transition-all duration-300 ${
                  userType === "parent"
                    ? 'text-white shadow-lg scale-105'
                    : 'bg-purple-50 text-purple-500 hover:bg-purple-100'
                }`}
                style={userType === "parent" ? { background: 'linear-gradient(90deg, #7357C9, #4B3F8C)', boxShadow: '0 4px 14px rgba(115,87,201,0.45)' } : {}}
              >
                Ibu Bapa
              </button>
              <button
                onClick={() => setUserType("staff")}
                data-testid="login-tab-staff"
                className={`flex-1 py-3 px-4 rounded-2xl font-bold text-sm transition-all duration-300 ${
                  userType === "staff"
                    ? 'text-white shadow-lg scale-105'
                    : 'bg-cyan-50 text-teal-600 hover:bg-cyan-100'
                }`}
                style={userType === "staff" ? { background: 'linear-gradient(90deg, #3FC9BC, #4B3F8C)', boxShadow: '0 4px 14px rgba(63,201,188,0.45)' } : {}}
              >
                Penyelia/Pendidik
              </button>
            </div>
          )}

          {/* PARENT LOGIN */}
          {mode === "login" && userType === "parent" && (
            <form onSubmit={handleParentLogin} className="space-y-4">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4 flex items-center justify-center gap-2">
                <span
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, #7357C9, #4B3F8C)',
                    boxShadow: '0 0 14px rgba(115,87,201,0.55)',
                  }}
                >
                  <Heart size={20} color="#FF6B9D" fill="#FF6B9D" />
                </span>
                Log Masuk Ibu Bapa
              </h2>
              
              {/* Biometric Quick Login Button - Show if supported and enabled */}
              {biometricLoginOn && biometricSupported && biometricEnabled && canUseBiometricLogin() && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    className="w-full py-4 bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-white font-black text-lg rounded-2xl shadow-lg shadow-emerald-200/50 transition-all hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-3"
                    data-testid="biometric-login-btn"
                  >
                    {biometricLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                        Mengesahkan...
                      </span>
                    ) : (
                      <>
                        <BiometricIcon className="w-6 h-6" />
                        Log Masuk {getBiometricName()}
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-400 font-bold">ATAU</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                </div>
              )}
              
              {/* Biometric hint - hanya pakej FULL (modul biometric_login ON) */}
              {biometricModuleOn && biometricSupported && !biometricEnabled && (
                <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <BiometricIcon className="w-5 h-5" />
                    <span className="text-sm font-bold">
                      {getBiometricName()} tersedia!
                    </span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    Log masuk dahulu, kemudian aktifkan di Tetapan Profil untuk log masuk pantas.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Nombor Telefon</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                  <input
                    type="tel"
                    className="w-full pl-12 pr-4 py-4 border-3 border-purple-200 rounded-2xl focus:border-purple-400 focus:ring-4 focus:ring-purple-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={parentData.phone}
                    onChange={(e) => setParentData({...parentData, phone: e.target.value})}
                    required
                    data-testid="parent-phone-input"
                    placeholder="0123456789"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Kata Laluan</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                  <input
                    type="password"
                    className="w-full pl-12 pr-4 py-4 border-3 border-purple-200 rounded-2xl focus:border-purple-400 focus:ring-4 focus:ring-purple-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={parentData.password}
                    onChange={(e) => setParentData({...parentData, password: e.target.value})}
                    required
                    data-testid="parent-password-input"
                    placeholder="Masukkan kata laluan"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid="parent-login-submit"
                disabled={loading} 
                className="w-full py-4 font-black text-lg rounded-2xl shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
                style={{ background: 'linear-gradient(90deg, #FFC93C, #FFD34F)', color: '#2B2E6B', boxShadow: '0 0 18px rgba(255,201,60,0.55)' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    Memproses...
                  </span>
                ) : "Log Masuk"}
              </Button>

              <div className="text-center text-sm space-y-2 pt-2">
                {registrationOn && (
                  <>
                    <button type="button" onClick={() => setMode("register-parent")} className="text-purple-500 hover:text-purple-600 font-bold hover:underline transition-colors" data-testid="register-parent-link">
                      Belum daftar? Daftar di sini! ✨
                    </button>
                    <br />
                  </>
                )}
                <button type="button" onClick={() => setMode("forgot-password")} className="text-gray-400 hover:text-gray-500 font-bold hover:underline transition-colors">
                  Lupa kata laluan? 🔑
                </button>
              </div>
            </form>
          )}

          {/* STAFF LOGIN */}
          {mode === "login" && userType === "staff" && (
            <form onSubmit={handleStaffLogin} className="space-y-4">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4 flex items-center justify-center gap-2">
                <span
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, #3FC9BC, #2B2E6B)',
                    boxShadow: '0 0 14px rgba(63,201,188,0.55)',
                  }}
                >
                  <Users size={20} color="#FFD34F" />
                </span>
                Log Masuk Penyelia/Pendidik
              </h2>
              
              {/* Biometric Quick Login Button - Show if supported and enabled */}
              {biometricLoginOn && biometricSupported && biometricEnabled && canUseBiometricLogin() && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    className="w-full py-4 bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-white font-black text-lg rounded-2xl shadow-lg shadow-emerald-200/50 transition-all hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-3"
                    data-testid="staff-biometric-login-btn"
                  >
                    {biometricLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                        Mengesahkan...
                      </span>
                    ) : (
                      <>
                        <BiometricIcon className="w-6 h-6" />
                        Log Masuk {getBiometricName()}
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-400 font-bold">ATAU</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                </div>
              )}
              
              {/* Biometric hint - hanya pakej FULL (modul biometric_login ON) */}
              {biometricModuleOn && biometricSupported && !biometricEnabled && (
                <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <BiometricIcon className="w-5 h-5" />
                    <span className="text-sm font-bold">
                      {getBiometricName()} tersedia!
                    </span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    Log masuk dahulu, kemudian aktifkan di Tetapan Profil untuk log masuk pantas.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                  <input
                    type="email"
                    data-testid="staff-email-input"
                    className="w-full pl-12 pr-4 py-4 border-3 border-blue-200 rounded-2xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={staffData.email}
                    onChange={(e) => setStaffData({...staffData, email: e.target.value})}
                    required
                    placeholder="email@contoh.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Kata Laluan</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                  <input
                    type="password"
                    data-testid="staff-password-input"
                    className="w-full pl-12 pr-4 py-4 border-3 border-blue-200 rounded-2xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={staffData.password}
                    onChange={(e) => setStaffData({...staffData, password: e.target.value})}
                    required
                    placeholder="Masukkan kata laluan"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading} 
                data-testid="staff-login-submit"
                className="w-full py-4 font-black text-lg rounded-2xl shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
                style={{ background: 'linear-gradient(90deg, #FFC93C, #FFD34F)', color: '#2B2E6B', boxShadow: '0 0 18px rgba(255,201,60,0.55)' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    Memproses...
                  </span>
                ) : "Log Masuk"}
              </Button>

              <div className="text-center text-sm space-y-2 pt-2">
                {registrationOn && (
                  <>
                    <button type="button" onClick={() => setMode("register-staff")} className="text-blue-500 hover:text-blue-600 font-bold hover:underline transition-colors flex items-center justify-center gap-1 mx-auto" data-testid="register-staff-link">
                      Daftar sebagai Penyelia/Pendidik <TeacherIcon size="sm" />
                    </button>
                    <br />
                  </>
                )}
                <button type="button" onClick={() => setMode("forgot-password")} className="text-gray-400 hover:text-gray-500 font-bold hover:underline transition-colors">
                  Lupa kata laluan? 🔑
                </button>
              </div>
            </form>
          )}

          {/* PARENT REGISTRATION */}
          {mode === "register-parent" && (
            <form onSubmit={handleParentRegister} className="space-y-3">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4">
                👶 Pendaftaran Ibu Bapa
              </h2>
              
              <div className="bg-yellow-50 border-3 border-yellow-300 rounded-2xl p-3 text-sm text-yellow-700 font-bold">
                📱 Nombor telefon akan digunakan untuk log masuk
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nombor Telefon *</label>
                <PhoneValidation
                  value={parentData.phone}
                  onChange={(value) => setParentData({...parentData, phone: value})}
                  onValidationChange={setPhoneValidationPassed}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama Anda *</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-3 border-orange-200 rounded-2xl focus:border-orange-400 outline-none font-bold"
                  value={parentData.name}
                  onChange={(e) => setParentData({...parentData, name: e.target.value})}
                  required
                  placeholder="Nama penuh"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama Pasangan</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-3 border-pink-200 rounded-2xl focus:border-pink-400 outline-none font-bold"
                  value={parentData.spouse_name}
                  onChange={(e) => setParentData({...parentData, spouse_name: e.target.value})}
                  placeholder="Nama suami/isteri (pilihan)"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Cawangan *</label>
                <select
                  className="w-full px-4 py-3 border-3 border-green-200 rounded-2xl focus:border-green-400 outline-none appearance-none bg-white font-bold"
                  value={parentData.branch}
                  onChange={(e) => setParentData({...parentData, branch: e.target.value})}
                  required
                >
                  <option value="">-- Pilih Cawangan --</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.name}>{branch.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Kata Laluan *</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border-3 border-purple-200 rounded-2xl focus:border-purple-400 outline-none font-bold"
                  value={parentData.password}
                  onChange={(e) => setParentData({...parentData, password: e.target.value})}
                  required
                  minLength={6}
                  placeholder="Minimum 6 aksara"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full py-4 bg-gradient-to-r from-green-400 to-cyan-400 text-white font-black rounded-2xl shadow-lg mt-2"
              >
                {loading ? "Memproses..." : "Daftar Sekarang 🎉"}
              </Button>

              <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-gray-500 font-bold hover:underline pt-2">
                ← Sudah ada akaun? Log masuk
              </button>
            </form>
          )}

          {/* STAFF REGISTRATION */}
          {mode === "register-staff" && (
            <form onSubmit={handleStaffRegister} className="space-y-3">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4 flex items-center justify-center gap-2">
                <TeacherIcon size="lg" /> Pendaftaran Penyelia/Pendidik
              </h2>
              
              <div className="bg-blue-50 border-3 border-blue-300 rounded-2xl p-3 text-sm text-blue-700 font-bold">
                ⚠️ Pendaftaran memerlukan kelulusan dari Superadmin
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama *</label>
                <input type="text" className="w-full px-4 py-3 border-3 border-blue-200 rounded-2xl focus:border-blue-400 outline-none font-bold" value={staffData.name} onChange={(e) => setStaffData({...staffData, name: e.target.value})} required placeholder="Nama penuh" />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Email *</label>
                <input type="email" className="w-full px-4 py-3 border-3 border-purple-200 rounded-2xl focus:border-purple-400 outline-none font-bold" value={staffData.email} onChange={(e) => setStaffData({...staffData, email: e.target.value})} required placeholder="email@contoh.com" />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Peranan *</label>
                <select className="w-full px-4 py-3 border-3 border-green-200 rounded-2xl focus:border-green-400 outline-none bg-white font-bold" value={staffData.role} onChange={(e) => setStaffData({...staffData, role: e.target.value})}>
                  <option value="guru">Guru</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Kata Laluan *</label>
                <input type="password" className="w-full px-4 py-3 border-3 border-orange-200 rounded-2xl focus:border-orange-400 outline-none font-bold" value={staffData.password} onChange={(e) => setStaffData({...staffData, password: e.target.value})} required minLength={6} placeholder="Minimum 6 aksara" />
              </div>

              <Button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-blue-400 to-purple-400 text-white font-black rounded-2xl shadow-lg mt-2">
                {loading ? "Memproses..." : "Hantar Permohonan 📨"}
              </Button>

              <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-gray-500 font-bold hover:underline pt-2">
                ← Kembali ke log masuk
              </button>
            </form>
          )}

          {/* FORGOT PASSWORD */}
          {mode === "forgot-password" && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4">
                🔑 Lupa Kata Laluan
              </h2>
              
              <div className="bg-cyan-50 border-3 border-cyan-300 rounded-2xl p-3 text-sm text-cyan-700 font-bold">
                📧 Link reset password akan dihantar ke email anda
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
                  <input
                    type="email"
                    className="w-full pl-12 pr-4 py-4 border-3 border-cyan-200 rounded-2xl focus:border-cyan-400 outline-none font-bold"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    placeholder="Masukkan email anda"
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-cyan-400 to-blue-400 text-white font-black rounded-2xl shadow-lg">
                {loading ? "Menghantar..." : "Hantar Link Reset 📩"}
              </Button>

              <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-gray-500 font-bold hover:underline pt-2">
                ← Kembali ke log masuk
              </button>
            </form>
          )}
        </div>

        {/* BUTANG VIDEO TUTORIAL */}
        <button
          type="button"
          onClick={() => setShowTutorial(true)}
          className="mt-5 mx-auto flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white/90 backdrop-blur border-2 border-purple-200 text-purple-600 font-black shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:border-purple-400 transition-all"
          data-testid="tutorial-video-btn"
        >
          <PlayCircle className="w-5 h-5" />
          Tonton Video Tutorial
        </button>
      </div>

      {/* DIALOG VIDEO TUTORIAL */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden rounded-2xl">
          <DialogHeader className="px-5 pt-4 pb-2">
            <DialogTitle className="flex items-center gap-2 text-purple-700 font-black">
              <PlayCircle className="w-5 h-5" />
              Video Tutorial Ibu Bapa
            </DialogTitle>
          </DialogHeader>
          <div className="px-5 pb-5">
            {tutorialUrl ? (
              isDirectVideo(tutorialUrl) ? (
                <video
                  controls
                  autoPlay
                  crossOrigin="anonymous"
                  className="w-full max-h-[65vh] object-contain rounded-xl bg-black"
                  data-testid="tutorial-video-player"
                >
                  <source src={tutorialUrl} />
                  {/* Sari kata telah dibakar (burned-in) terus dalam video tutorial */}
                </video>
              ) : (
                <iframe
                  src={getEmbedUrl(tutorialUrl)}
                  title="Video Tutorial"
                  className="w-full aspect-video rounded-xl bg-black"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="tutorial-video-iframe"
                />
              )
            ) : (
              <div className="w-full aspect-video rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-dashed border-purple-200 flex flex-col items-center justify-center gap-3 text-center p-6" data-testid="tutorial-video-placeholder">
                <PlayCircle className="w-14 h-14 text-purple-300" />
                <p className="font-black text-purple-500">Video tutorial akan tersedia tidak lama lagi!</p>
                <p className="text-sm text-gray-500 font-semibold">Sementara itu, ikuti panduan interaktif langkah demi langkah di bawah.</p>
              </div>
            )}

            {/* PAUTAN PANDUAN INTERAKTIF LANGKAH DEMI LANGKAH */}
            <button
              type="button"
              onClick={() => { setShowTutorial(false); window.location.href = "/tutorial-ibubapa"; }}
              className="mt-4 w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-black shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              data-testid="open-interactive-tutorial-btn"
            >
              <span className="flex items-center gap-2.5">
                <BookOpen className="w-5 h-5" />
                Panduan Interaktif Langkah Demi Langkah
              </span>
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Butang muat turun HD disembunyikan atas permintaan -
                fail masih boleh diakses terus di /tutorial-video-ibubapa-hd.mp4 */}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes floatDecor {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(10deg); }
          66% { transform: translateY(-10px) rotate(-5deg); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes borderFlow {
          0% { background-position: 0 0, 0% 50%; }
          100% { background-position: 0 0, 300% 50%; }
        }
        @keyframes iconFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
}
